/**
 * HVP Cascade Controller - variation -> DOM
 *
 * Paints/clears price, availability, SKU, image, add-to-cart state and the
 * variation_id input. applyVariation() also fires the jQuery
 * found_variation event the PrintCart designer depends on to unlock.
 *
 * Split out of cascade-controller.js (350-line rule): the methods below moved
 * VERBATIM and are installed on HVPCascadeController.prototype by
 * cascade-controller.js, so `this` is the same instance and behavior is identical.
 *
 * @package HaruVariationsPro
 * @version 1.0.0
 */

export const CascadeApplyMethods = {
    /**
     * Apply variation to form
     */
    applyVariation(variation) {
        window.hvpData && window.hvpData.debug && console.log('[HVP] applyVariation called with:', variation.variation_id, 'price_html:', variation.price_html?.substring(0, 50));

        // Find or create the single_variation container
        let singleVariation = this.form.querySelector('.single_variation');
        if (!singleVariation) {
            singleVariation = this.form.querySelector('.single_variation_wrap');
        }

        // Ensure we have a price container inside single_variation
        let priceContainer = document.querySelector('.woocommerce-variation-price');
        if (!priceContainer && singleVariation) {
            // Create the price container structure WooCommerce expects
            priceContainer = document.createElement('div');
            priceContainer.className = 'woocommerce-variation-price';
            singleVariation.insertBefore(priceContainer, singleVariation.firstChild);
        }

        // Update price
        if (priceContainer && variation.price_html) {
            priceContainer.innerHTML = variation.price_html;
            window.hvpData && window.hvpData.debug && console.log('[HVP] Price updated:', variation.price_html.substring(0, 50));
        } else if (!priceContainer) {
            window.hvpData && window.hvpData.debug && console.warn('[HVP] No price container found, trying .summary .price');
            // Fallback to summary price
            const summaryPrice = document.querySelector('.summary .price, .product .price');
            if (summaryPrice && variation.price_html) {
                summaryPrice.innerHTML = variation.price_html;
            }
        }

        // Ensure we have an availability container
        let availabilityContainer = document.querySelector('.woocommerce-variation-availability');
        if (!availabilityContainer && singleVariation) {
            availabilityContainer = document.createElement('div');
            availabilityContainer.className = 'woocommerce-variation-availability';
            singleVariation.appendChild(availabilityContainer);
        }

        // Update availability
        if (availabilityContainer) {
            if (variation.is_in_stock) {
                availabilityContainer.innerHTML = '<p class="stock in-stock">In stock</p>';
            } else {
                availabilityContainer.innerHTML = '<p class="stock out-of-stock">Out of stock</p>';
            }
        }

        // Update image
        if (variation.image) {
            this.updateProductImage(variation.image);
        }

        // Update SKU
        const skuElement = document.querySelector('.sku_wrapper .sku, .product_meta .sku');
        if (skuElement) {
            if (variation.sku) {
                skuElement.textContent = variation.sku;
                window.hvpData && window.hvpData.debug && console.log('[HVP] SKU updated to:', variation.sku);
            } else {
                // SKU not in variation data - this is expected if cache wasn't rebuilt
                window.hvpData && window.hvpData.debug && console.log('[HVP] No SKU in variation data');
            }
        }

        // Update add to cart button
        const addToCartBtn = this.form.querySelector('.single_add_to_cart_button');
        if (addToCartBtn) {
            // Check if variation is purchasable (has price and is in stock)
            const isPurchasable = variation.is_in_stock &&
                                  variation.is_purchasable !== false &&
                                  (variation.has_price || variation.price > 0 || variation.price_html);

            if (isPurchasable) {
                addToCartBtn.disabled = false;
                addToCartBtn.classList.remove('disabled', 'wc-variation-selection-needed');
                addToCartBtn.classList.add('hvp-ready');
                window.hvpData && window.hvpData.debug && console.log('[HVP] Add to cart button ENABLED');
            } else {
                addToCartBtn.disabled = true;
                addToCartBtn.classList.add('disabled');
                addToCartBtn.classList.remove('hvp-ready');
                window.hvpData && window.hvpData.debug && console.log('[HVP] Add to cart button DISABLED - purchasable:', variation.is_purchasable, 'in_stock:', variation.is_in_stock);
            }
        }

        // Update variation_id input
        const variationInput = this.form.querySelector('input[name="variation_id"]');
        if (variationInput) {
            variationInput.value = variation.variation_id;

            // INTEGRATION FIX: Trigger change event for plugins that listen to variation_id changes
            // (e.g., NBDesigner/Printcart needs this to enable the design button)
            if (typeof jQuery !== 'undefined') {
                jQuery(variationInput).trigger('change');
            }
        }

        // Show variation container
        const variationContainer = this.form.querySelector('.woocommerce-variation, .single_variation');
        if (variationContainer) {
            variationContainer.style.display = '';
        }

        // CRITICAL: Trigger WooCommerce's found_variation event
        // This allows WooCommerce to render the price template and
        // enables other plugins (like TAV) to respond to variation changes
        if (typeof jQuery !== 'undefined') {
            jQuery(this.form).trigger('found_variation', [variation]);
        }
    },

    /**
     * Clear variation display
     */
    clearVariation() {
        const priceContainer = document.querySelector('.woocommerce-variation-price, .single_variation_wrap .price');
        if (priceContainer) {
            priceContainer.innerHTML = '';
        }

        const availabilityContainer = document.querySelector('.woocommerce-variation-availability');
        if (availabilityContainer) {
            availabilityContainer.innerHTML = '';
        }

        // Reset SKU to parent product SKU or 'N/A'
        const skuElement = document.querySelector('.sku_wrapper .sku, .product_meta .sku');
        if (skuElement) {
            // Store original SKU on first clear if not already stored
            if (!this._originalSku && skuElement.dataset.originalSku) {
                this._originalSku = skuElement.dataset.originalSku;
            } else if (!this._originalSku) {
                // First time - store current value as original
                this._originalSku = skuElement.textContent;
                skuElement.dataset.originalSku = this._originalSku;
            }
            skuElement.textContent = this._originalSku || 'N/A';
        }

        const addToCartBtn = this.form.querySelector('.single_add_to_cart_button');
        if (addToCartBtn) {
            addToCartBtn.disabled = true;
            addToCartBtn.classList.add('disabled', 'wc-variation-selection-needed');
            addToCartBtn.classList.remove('hvp-ready');
        }

        const variationInput = this.form.querySelector('input[name="variation_id"]');
        if (variationInput) {
            variationInput.value = '';
        }

        const variationContainer = this.form.querySelector('.woocommerce-variation, .single_variation');
        if (variationContainer) {
            variationContainer.style.display = 'none';
        }
    },

    /**
     * Update product image
     */
    updateProductImage(imageData) {
        const mainImage = document.querySelector('.woocommerce-product-gallery__image img, .flex-active-slide img');

        if (mainImage && (imageData.url || imageData.src)) {
            mainImage.src = imageData.url || imageData.src;

            if (imageData.srcset) {
                mainImage.srcset = imageData.srcset;
            }

            if (imageData.alt) {
                mainImage.alt = imageData.alt;
            }
        }
    },
};

export default CascadeApplyMethods;
